import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SortIcon from '@mui/icons-material/Sort';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import BackButton from '../../components/BackButton';
// [CASH-MODE] UPI / Cash filter + split
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam } from '../../utils/paymentMode';

const TotalShopCollectionReport = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [collections, setCollections] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');

  // Dropdown lists
  const [allStateHeads, setAllStateHeads] = useState([]);
  const [allQpayCenters, setAllQpayCenters] = useState([]);
  const [allDistrictsList, setAllDistrictsList] = useState([]);
  const [allConstituenciesList, setAllConstituenciesList] = useState([]);

  // Filters State
  const [districtFilter, setDistrictFilter] = useState('all');
  const [villageFilter, setVillageFilter] = useState('all');
  const [stateHeadFilter, setStateHeadFilter] = useState('all');
  const [qpayCenterFilter, setQpayCenterFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [apiTotalsByMode, setApiTotalsByMode] = useState(null); // [CASH-MODE]

  // Daily Detail Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
        setUserLevel(decodedToken.level);
      } catch (error) {
        console.error('Error decoding token:', error);
        setDialogMessage('Error with authentication. Please login again.');
        setOpenDialog(true);
      }
    } else {
      setDialogMessage('Authentication required. Please login.');
      setOpenDialog(true);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchDropdownOptions();
      fetchCollectionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userId, shopId, districtFilter, villageFilter, stateHeadFilter, qpayCenterFilter, paymentMode]); // [CASH-MODE] paymentMode

  const fetchDropdownOptions = async () => {
    try {
      const [userRes, districtRes, shopRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/users-with-pincodes/${userId}`),
        axios.get(`${API_BASE_URL}/districts`),
        axios.get(`${API_BASE_URL}/shops/${userId}?progress=6&page=1&limit=500`),
      ]);

      if (userRes.data && Array.isArray(userRes.data)) {
        const sh = userRes.data.filter((u) => u.level === 2);
        const qc = userRes.data.filter((u) => u.level === 4);
        setAllStateHeads(sh);
        setAllQpayCenters(qc);
      }

      if (districtRes.data && Array.isArray(districtRes.data)) {
        const dists = districtRes.data.map((d) => d.districtName || d.name).filter(Boolean);
        setAllDistrictsList(Array.from(new Set(dists)));
      }

      if (shopRes.data && shopRes.data.shops) {
        const consts = shopRes.data.shops
          .map((s) => s.village || s.constituency || s.city || s.taluk)
          .filter(Boolean);
        setAllConstituenciesList(Array.from(new Set(consts)));
      }
    } catch (e) {
      console.error('Error fetching dropdown options:', e);
    }
  };

  const fetchCollectionData = async () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return;
    }
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before end date.');
      setOpenDialog(true);
      return;
    }

    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      const token = sessionStorage.getItem('token');

      const payload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        userId,
        shopId: shopId || 'all',
        district: districtFilter,
        village: villageFilter,
        stateHeadId: stateHeadFilter,
        qpayCenterId: qpayCenterFilter,
        paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
      };

      const response = await axios.post(
        `${API_BASE_URL}/shopReport/totalShopCollection`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        const rawColls = data.collectionsByDate || [];
        setCollections(rawColls);
        setGrandTotal(data.grandTotal || 0);
        setApiTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
      } else {
        setCollections([]);
        setGrandTotal(0);
        setApiTotalsByMode(null); // [CASH-MODE]
      }
    } catch (error) {
      console.error('Error fetching collection data:', error);
      setCollections([]);
      setGrandTotal(0);
      setApiTotalsByMode(null); // [CASH-MODE]
    } finally {
      setLoading(false);
    }
  };

  // Filter lists for dropdowns combining API and fetched data
  const combinedDistricts = Array.from(
    new Set([...allDistrictsList, ...collections.map((c) => c.district).filter(Boolean)])
  );
  const combinedConstituencies = Array.from(
    new Set([...allConstituenciesList, ...collections.map((c) => c.village).filter(Boolean)])
  );

  // Filtered & Sorted collections
  const filteredCollections = collections
    .filter((item) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase().trim();
        const mId = String(item.merchantId || '').toLowerCase();
        const mob = String(item.mobile || '').toLowerCase();
        const email = String(item.email || '').toLowerCase();
        const mName = String(item.merchantName || '').toLowerCase();
        const sName = String(item.shopName || '').toLowerCase();

        const match =
          mId.includes(term) ||
          mob.includes(term) ||
          email.includes(term) ||
          mName.includes(term) ||
          sName.includes(term);
        if (!match) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

  const totalDailyCollectionSum = filteredCollections.reduce(
    (sum, c) => sum + (c.dailyCollection || c.amount || 0),
    0
  );
  const totalDeductCommissionSum = filteredCollections.reduce(
    (sum, c) => sum + (c.deductCommission || 0),
    0
  );
  const totalBalanceSum = totalDailyCollectionSum - totalDeductCommissionSum;

  // [CASH-MODE] per-date UPI / Cash split (only when the API sends it)
  const hasModeSplit = collections.some((c) => c && (c.upiAmount !== undefined || c.cashAmount !== undefined));
  const modeTotals = hasModeSplit
    ? {
        UPI: { amount: filteredCollections.reduce((sum, c) => sum + (Number(c.upiAmount) || 0), 0) },
        CASH: { amount: filteredCollections.reduce((sum, c) => sum + (Number(c.cashAmount) || 0), 0) },
      }
    : apiTotalsByMode;
  const modeTotalsKey = (() => {
    const t = modeTotals && (modeTotals.UPI || modeTotals.CASH);
    if (!t || t.amount !== undefined) return 'amount';
    return Object.keys(t).find((k) => k !== 'count' && typeof t[k] === 'number') || 'amount';
  })();

  const handleOpenDetailModal = (record) => {
    setSelectedRecord(record);
    setDetailModalOpen(true);
  };

  const handleExportCSV = () => {
    if (!filteredCollections || filteredCollections.length === 0) return;
    const headers = [
      'SL No',
      'Date',
      'Merchant ID',
      'Merchant Name',
      'Shop Name',
      'Mobile',
      'Email',
      'District',
      'Constituency',
      'State Head ID',
      'Q-Pay Center ID',
      'Daily Collection',
      'Deduct Commission',
      'Balance',
      ...(hasModeSplit ? ['UPI', 'Cash'] : []), // [CASH-MODE]
    ];
    const csvRows = [headers.join(',')];

    filteredCollections.forEach((r, idx) => {
      const daily = r.dailyCollection || r.amount || 0;
      const comm = r.deductCommission || 0;
      const bal = r.balance !== undefined ? r.balance : daily - comm;

      csvRows.push(
        [
          idx + 1,
          `"${r.date}"`,
          `"${r.merchantId || 'N/A'}"`,
          `"${r.merchantName || 'N/A'}"`,
          `"${r.shopName || 'N/A'}"`,
          `"${r.mobile || 'N/A'}"`,
          `"${r.email || 'N/A'}"`,
          `"${r.district || 'N/A'}"`,
          `"${r.village || 'N/A'}"`,
          `"${r.stateHeadId || 'N/A'}"`,
          `"${r.qpayCenterId || 'N/A'}"`,
          daily.toFixed(2),
          comm.toFixed(2),
          bal.toFixed(2),
          ...(hasModeSplit ? [(Number(r.upiAmount) || 0).toFixed(2), (Number(r.cashAmount) || 0).toFixed(2)] : []), // [CASH-MODE]
        ].join(',')
      );
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Total_Shop_Collection_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleExportPDF = () => {
    if (!filteredCollections || filteredCollections.length === 0) return;
    try {
      const doc = new jsPDF('landscape');
      doc.text('Total Shop Collection Report (Merchants Daily Report)', 14, 15);
      const tableColumn = [
        'SL No',
        'Date',
        'Merchant ID',
        'Merchant Name',
        'Mobile',
        'District',
        'Daily Collection',
        'Deduct Commission',
        'Balance',
        ...(hasModeSplit ? ['UPI', 'Cash'] : []), // [CASH-MODE]
      ];
      const tableRows = [];

      filteredCollections.forEach((r, idx) => {
        const daily = r.dailyCollection || r.amount || 0;
        const comm = r.deductCommission || 0;
        const bal = r.balance !== undefined ? r.balance : daily - comm;

        tableRows.push([
          idx + 1,
          format(new Date(r.date), 'dd-MM-yyyy'),
          r.merchantId || 'N/A',
          r.merchantName || 'N/A',
          r.mobile || 'N/A',
          r.district || 'N/A',
          `₹${daily.toFixed(2)}`,
          `₹${comm.toFixed(2)}`,
          `₹${bal.toFixed(2)}`,
          ...(hasModeSplit ? [`₹${(Number(r.upiAmount) || 0).toFixed(2)}`, `₹${(Number(r.cashAmount) || 0).toFixed(2)}`] : []), // [CASH-MODE]
        ]);
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 23, 42] },
      });
      doc.save(`Total_Collection_Report_${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      handlePrint();
    }
  };

  const handlePrint = () => {
    if (!filteredCollections || filteredCollections.length === 0) return;
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <html>
        <head>
          <title>Total Shop Collection Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #1e293b; }
            h2 { text-align: center; margin-bottom: 5px; color: #0f172a; }
            p.sub { text-align: center; color: #64748b; font-size: 13px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 10px; text-align: left; font-size: 12px; }
            th { background-color: #f1f5f9; font-weight: bold; }
            tr:nth-child(even) { background-color: #f8fafc; }
            .total-box { margin-top: 20px; text-align: right; font-weight: bold; font-size: 14px; }
          </style>
        </head>
        <body>
          <h2>Total Shop Collection Report</h2>
          <p class="sub">From ${format(startDate, 'dd-MM-yyyy')} to ${format(endDate, 'dd-MM-yyyy')}</p>
          <table>
            <thead>
              <tr>
                <th>SL No</th>
                <th>Date</th>
                <th>Merchant ID</th>
                <th>Merchant Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Daily Collection</th>
                <th>Deduct Commission</th>
                <th>Balance</th>
                ${hasModeSplit ? '<th>UPI</th><th>Cash</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${filteredCollections
                .map((r, i) => {
                  const daily = r.dailyCollection || r.amount || 0;
                  const comm = r.deductCommission || 0;
                  const bal = r.balance !== undefined ? r.balance : daily - comm;
                  return `
                <tr>
                  <td>${i + 1}</td>
                  <td>${format(new Date(r.date), 'dd-MM-yyyy')}</td>
                  <td>${r.merchantId || 'N/A'}</td>
                  <td>${r.merchantName || 'N/A'}</td>
                  <td>${r.mobile || 'N/A'}</td>
                  <td>${r.email || 'N/A'}</td>
                  <td>₹${daily.toFixed(2)}</td>
                  <td>₹${comm.toFixed(2)}</td>
                  <td>₹${bal.toFixed(2)}</td>
                  ${hasModeSplit ? `<td>₹${(Number(r.upiAmount) || 0).toFixed(2)}</td><td>₹${(Number(r.cashAmount) || 0).toFixed(2)}</td>` : ''}
                </tr>
              `;
                })
                .join('')}
            </tbody>
          </table>
          <div class="total-box">
            Total Collection: ₹${totalDailyCollectionSum.toFixed(2)} | Deduct Commission: ₹${totalDeductCommissionSum.toFixed(2)} | Net Balance: ₹${totalBalanceSum.toFixed(2)}
          </div>
          <script>
            window.onload = function() { window.print(); window.close(); };
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'SL No',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const index = filteredCollections.findIndex(
          (c) => c.date === params.row.date && c.merchantId === params.row.merchantId
        );
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : params.row.id}
          </Typography>
        );
      },
    },
    {
      field: 'dateFormatted',
      headerName: 'Date',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {format(new Date(params.row.date), 'dd-MM-yyyy')}
        </Typography>
      ),
    },
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'dailyCollection',
      headerName: 'Daily collection',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
          ₹{(params.row.dailyCollection || params.row.amount || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'deductCommission',
      headerName: 'Deduct Commission',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
          ₹{(params.row.deductCommission || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 160,
      renderCell: (params) => {
        const daily = params.row.dailyCollection || params.row.amount || 0;
        const comm = params.row.deductCommission || 0;
        const bal = params.row.balance !== undefined ? params.row.balance : daily - comm;
        return (
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0284c7' }}>
            ₹{bal.toFixed(2)}
          </Typography>
        );
      },
    },
    // [CASH-MODE] UPI / Cash split columns (hidden when API does not send them)
    ...(hasModeSplit
      ? [
          {
            field: 'upiAmount',
            headerName: 'UPI',
            width: 130,
            renderCell: (params) => (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                ₹{(Number(params.row.upiAmount) || 0).toFixed(2)}
              </Typography>
            ),
          },
          {
            field: 'cashAmount',
            headerName: 'Cash',
            width: 130,
            renderCell: (params) => (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                ₹{(Number(params.row.cashAmount) || 0).toFixed(2)}
              </Typography>
            ),
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: 'Action Button',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          color="info"
          startIcon={<VisibilityIcon />}
          onClick={() => handleOpenDetailModal(params.row)}
          sx={{ fontWeight: 600 }}
        >
          Details
        </Button>
      ),
    },
  ];

  const rows = filteredCollections.map((item, index) => ({
    id: index + 1,
    ...item,
  }));

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br />
      <br />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ width: '100%', position: 'relative' }}>
          {/* Header Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  Total Shop Collection Report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Merchants Daily Collections & Commission Deductions
                </Typography>
              </Box>

              {/* Action Buttons: CSV Export (Level 1 Only) */}
              {userLevel === 1 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCSV}
                    disabled={filteredCollections.length === 0}
                    sx={{ fontWeight: 700 }}
                  >
                    Export to CSV
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Filter Section */}
            <Grid container spacing={2} alignItems="center">
              {/* Date Filters */}
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                  maxDate={new Date()}
                  inputFormat="dd-MM-yyyy"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                  maxDate={new Date()}
                  inputFormat="dd-MM-yyyy"
                />
              </Grid>

              {/* Search by Merchant ID / Mobile / Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by Merchant ID, Mobile or Email"
                  placeholder="Search Merchant ID, Mobile, Email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              {/* District wise filter */}
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>District wise</InputLabel>
                  <Select
                    value={districtFilter}
                    label="District wise"
                    onChange={(e) => setDistrictFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Districts</MenuItem>
                    {combinedDistricts.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Constituency wise filter */}
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Constituency wise</InputLabel>
                  <Select
                    value={villageFilter}
                    label="Constituency wise"
                    onChange={(e) => setVillageFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Constituencies</MenuItem>
                    {combinedConstituencies.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* State Head wise filter (Level 2 Users) */}
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>State Head wise</InputLabel>
                  <Select
                    value={stateHeadFilter}
                    label="State Head wise"
                    onChange={(e) => setStateHeadFilter(e.target.value)}
                  >
                    <MenuItem value="all">All State Heads</MenuItem>
                    {allStateHeads.map((sh) => {
                      const val = sh.stateHeadId || sh.displayId || sh.id;
                      return (
                        <MenuItem key={sh.id} value={val}>
                          {sh.fullName} (ID: {val})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* Q-Pay Centre wise filter (Level 4 Users) */}
              <Grid item xs={6} sm={2.2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Q-Pay Centre wise</InputLabel>
                  <Select
                    value={qpayCenterFilter}
                    label="Q-Pay Centre wise"
                    onChange={(e) => setQpayCenterFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Q-Pay Centers</MenuItem>
                    {allQpayCenters.map((qc) => {
                      const val = qc.stateHeadId || qc.displayId || qc.id;
                      return (
                        <MenuItem key={qc.id} value={val}>
                          {qc.fullName} (ID: {val})
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* [CASH-MODE] Payment Mode filter */}
              <Grid item xs={6} sm={3}>
                <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
              </Grid>

              {/* Sort by Date Toggle */}
              <Grid item xs={12} sm={0.8} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip title={`Sort by Date (${sortOrder === 'desc' ? 'Newest First' : 'Oldest First'})`}>
                  <IconButton
                    color="primary"
                    onClick={() => setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                    sx={{ border: '1px solid #cbd5e1', borderRadius: 2 }}
                  >
                    <SortIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* Table / Loader */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <React.Fragment>
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  rowHeight={60}
                />
              </Box>

              {/* Summary Paper */}
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Showing {filteredCollections.length} Collection Record(s)
                </Typography>

                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                    Total Daily Collection: ₹{totalDailyCollectionSum.toFixed(2)}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main' }}>
                    Total Deduct Commission: ₹{totalDeductCommissionSum.toFixed(2)}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800, color: '#0284c7' }}>
                    Net Balance: ₹{totalBalanceSum.toFixed(2)}
                  </Typography>
                </Box>
              </Paper>

              {/* [CASH-MODE] UPI vs Cash collection */}
              {modeTotals && <PaymentModeTotals totals={modeTotals} amountKey={modeTotalsKey} title="Collection" />}
            </React.Fragment>
          )}

          {/* Record Detail Modal */}
          <Dialog
            open={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                Daily Collection Details
              </Typography>
              <IconButton size="small" onClick={() => setDetailModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent dividers>
              {selectedRecord && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Date:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {format(new Date(selectedRecord.date), 'dd-MM-yyyy')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Merchant ID:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                      {selectedRecord.merchantId || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Merchant / Shop Name:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.merchantName || 'N/A'} ({selectedRecord.shopName || 'N/A'})
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Mobile:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.mobile || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Email:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.email || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">District:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.district || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Constituency:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.village || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}><Divider /></Grid>
                  {/* [CASH-MODE] */}
                  {(selectedRecord.upiAmount !== undefined || selectedRecord.cashAmount !== undefined) && (
                    <React.Fragment>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">UPI:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          ₹{(Number(selectedRecord.upiAmount) || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">Cash:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                          ₹{(Number(selectedRecord.cashAmount) || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                    </React.Fragment>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Daily Collection:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'success.main' }}>
                      ₹{(selectedRecord.dailyCollection || selectedRecord.amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Deduct Commission:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main' }}>
                      ₹{(selectedRecord.deductCommission || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Balance:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0284c7' }}>
                      ₹{((selectedRecord.dailyCollection || selectedRecord.amount || 0) - (selectedRecord.deductCommission || 0)).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setDetailModalOpen(false)} variant="contained" color="primary" sx={{ fontWeight: 700 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle sx={{ fontWeight: 700 }}>Message</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="primary" variant="contained">
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalShopCollectionReport;